import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, type TokenPair } from "@evocha/api-client";
import { dealerApi, DEALER_STORAGE_KEY } from "./api";

export interface DealerProfile {
  id: string;
  code: string;
  business_name: string;
  owner_name: string | null;
  phone: string;
  email: string | null;
  region: string | null;
  district: string | null;
  state: string;
  wallet_id: string | null;
  daily_transfer_limit_minor: number;
  txn_transfer_limit_minor: number;
  created_at: string | null;
}

export interface DealerAuthSession extends TokenPair {
  dealer: DealerProfile;
}

interface StoredSession extends TokenPair {
  dealer?: DealerProfile;
}

interface SessionValue {
  dealer: DealerProfile | null;
  authenticated: boolean;
  loading: boolean;
  signIn: (session: DealerAuthSession) => void;
  updateDealer: (dealer: DealerProfile) => void;
  signOut: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(DEALER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return parsed?.access_token ? parsed : null;
  } catch {
    return null;
  }
}

function saveSession(session: StoredSession | null) {
  try {
    if (session) localStorage.setItem(DEALER_STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(DEALER_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());
  const [loading, setLoading] = useState<boolean>(() => {
    const stored = loadSession();
    return Boolean(stored?.access_token && !stored.dealer);
  });

  useEffect(() => {
    const stored = loadSession();
    if (!stored?.access_token || stored.dealer) {
      setLoading(false);
      return;
    }
    let alive = true;
    dealerApi
      .get<{ dealer: DealerProfile }>("/superdealer/v1/me")
      .then((me) => {
        if (!alive) return;
        const next: StoredSession = { ...stored, dealer: me.dealer };
        saveSession(next);
        setSession(next);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
          saveSession(null);
          setSession(null);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const signIn = useCallback(
    (next: DealerAuthSession) => {
      const stored: StoredSession = {
        access_token: next.access_token,
        refresh_token: next.refresh_token,
        token_type: next.token_type,
        expires_in: next.expires_in,
        refresh_expires_in: next.refresh_expires_in,
        dealer: next.dealer,
      };
      saveSession(stored);
      setSession(stored);
      setLoading(false);
      queryClient.clear();
    },
    [queryClient],
  );

  const updateDealer = useCallback(
    (dealer: DealerProfile) => {
      const stored = loadSession();
      if (!stored) return;
      const next = { ...stored, dealer };
      saveSession(next);
      setSession(next);
    },
    [],
  );

  const signOut = useCallback(() => {
    const stored = loadSession();
    void dealerApi
      .post("/superdealer/v1/auth/logout", { refresh_token: stored?.refresh_token })
      .catch(() => undefined);
    saveSession(null);
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<SessionValue>(
    () => ({
      dealer: session?.dealer ?? null,
      authenticated: Boolean(session?.access_token),
      loading,
      signIn,
      updateDealer,
      signOut,
    }),
    [session, loading, signIn, updateDealer, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useDealerSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useDealerSession must be used inside SessionProvider");
  return ctx;
}
